import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, App, Button, Col, Form, Input, Modal, Row, Spin, Tabs } from 'antd';
import { RuleObject } from 'antd/es/form';
import { guid } from 'odata-query';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrganizationsSelect from 'src/common/components/business/organization-select';
import UserAutocomplete from 'src/common/components/business/user-autocomplete';
import { curateString } from 'src/core/utils/object';
import { isEmailOrGuid, nameof } from 'src/core/utils/object';
import { container } from 'src/inversify.config';
import RoleAssignmentAttributesComponent from 'src/pages/roles/role-attributes/role-attributes';
import { CreateGroupItem, GroupItem, GroupItemStore, GroupType } from 'src/stores/groups-store';
import {
  CreateStructmasterLicenseItem,
  StructmasterLicenseItem,
  StructmasterLicensesStore,
  UnsignedStructmasterLicense,
} from 'src/stores/licensing-store';
import { OrganizationItemStore, ORGANIZATIONS_MEMBER_LIMIT } from 'src/stores/organizations-store';
import { RoleSummaryStore, StructmasterUserRoleName } from 'src/stores/roles-store';
import { UserSummaryStore } from 'src/stores/users-store';

type StructmasterLicenseGroupEditProps = {
  onClose: (reload?: boolean) => void;
  id: string;
  disabled?: boolean;
  editable?: boolean;
  unsigned?: boolean;
  canApproveLicenses: boolean;
};

const StructmasterLicenseGroupEdit: FC<StructmasterLicenseGroupEditProps> = ({ onClose, id, disabled, editable, unsigned, canApproveLicenses }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [customError, setCustomError] = useState('');

  const groupStore = container.get(GroupItemStore);
  const groupState = groupStore.state;

  const roleAssignmentStore = container.get(StructmasterLicensesStore);

  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;

  const [licenseItem, setLicenseItem] = useState<StructmasterLicenseItem | UnsignedStructmasterLicense>();

  const disabledUsersRef = useRef<string[]>([]);
  const [organizationId, setOrganizationId] = useState<string>();
  const [licenseName, setLicenseName] = useState<string>();
  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [currentOrganizationMembers, setCurrentOrganizationMembers] = useState<string[]>([]);
  const [groupUserCount, setGroupUserCount] = useState<number>(0);
  const [formUsers, setFormUsers] = useState<{ label: string; value: string }[]>([]);
  const { modal } = App.useApp();

  const userStore = container.get(UserSummaryStore);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      let item = await roleAssignmentStore.getLicense(id, unsigned);
      const members = item?.members;

      if (members) {
        const updatedMemberData = await userStore.getUpdatedUserIds(members.map((v) => v?.idOrEmail ?? v.memberId));
        if (updatedMemberData) {
          item.members = item.members.map((member) => ({
            memberId: member.memberId,
            name: member.name,
            idOrEmail: updatedMemberData[member.idOrEmail] ?? member.memberId,
          }));
        }
        if (unsigned) setFormUsers(item.members.map((m) => ({ label: m.name, value: m?.idOrEmail ?? m?.memberId })));
      }

      if (item) setLicenseItem(item);
      if (item?.organizationId) {
        await organizationStore.load(item?.organizationId);
        setCurrentOrganizationMembers((organizationState?.item?.value?.members ?? [])?.map((v) => v.userId) ?? []);
        setOrganizationId(item?.organizationId);
        setLicenseName(item.licenseName);
        const users = await roleAssignmentStore.getUsersAlreadyOnGroupType(item?.organizationId, GroupType.Structmaster, item.id);
        if (users)
          disabledUsersRef.current = users.filter((disabledUser) => unsigned || !item.members.some((member) => member.idOrEmail === disabledUser));
      }
      setIsLoading(false);
    };

    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUnsignedLicense = async (item: UnsignedStructmasterLicense) => {
    const response = await roleAssignmentStore.saveUnsignedLicense(licenseItem?.id!, item, licenseItem);
    if (response?.error) {
      setCustomError(t('ErrorUpdatingLicense'));
    } else {
      onClose(response?.isSuccess);
    }
  };

  const updateLicense = async (item: GroupItem, expirationDate: Date) => {
    const response = await roleAssignmentStore.saveLicense(licenseItem?.id!, item, licenseItem);
    if (response?.error) {
      setCustomError(t('ErrorUpdatingLicense'));
    } else {
      onClose(response?.isSuccess);
    }
  };

  const signOffLicense = async (item: CreateGroupItem, expirationDate: Date) => {
    modal.confirm({
      title: t('SignOffStructmasterLicenseWarningTitle'),
      content: t('SignOffStructmasterLicenseWarningMessage'),
      onOk: async () => {
        setIsLoading(true);

        await rolesStore.load({ filter: { organizationId: guid(organizationId), name: StructmasterUserRoleName }, take: 1, skip: 0 });
        const roleId = [...rolesState.items.value].firstOrDefault()?.id!;

        const postItem: CreateStructmasterLicenseItem = {
          createGroupBody: item,
          expirationDate,
          roleId,
        };

        let response = await roleAssignmentStore.signOff(id, postItem);

        if (!response?.isSuccess || response?.error) {
          setCustomError(t('ErrorUpdatingLicense'));
          setIsLoading(false);
          return;
        }

        setIsLoading(false);
        if (response?.isSuccess) {
          form.resetFields();
          if (response?.error) {
            setCustomError(t('ErrorUpdatingLicense'));
          } else {
            onClose(response?.isSuccess);
          }
        }
      },
    });
  };

  const onEdit = async (approve: boolean) => {
    if (disabled) {
      onClose();
      return;
    }
    if (!licenseItem) return;

    let item: any;
    try {
      item = (await form.validateFields()) as any;
    } catch {
      setCustomError(t('CheckLicenseInfoAndUsersTabs'));
      return;
    }

    const groupName = curateString(item.name);
    const duplicatedGroups = await roleAssignmentStore.getStructmasterLicenses({
      query: {
        filter: { LicenseName: groupName, OrganizationId: guid(organizationId) },
      },
    });

    if (duplicatedGroups?.count && groupName !== licenseName) {
      setCustomError(t('ThereIsAlreadyAStructmasterLicenseWithThisName'));
      return;
    }

    setCustomError('');

    if (!unsigned) {
      setIsLoading(true);
      const groupItem = {
        id: licenseItem.id,
        name: curateString(item.name),
        organizationId: item.organization.value,
        members: item.members.map((v: { user: { value: string; label: string } }) => ({ memberId: v.user.value, name: v.user.label })),
      } as GroupItem;

      await updateLicense(groupItem, item.expirationDate);
      setIsLoading(false);
      return;
    }

    if (!approve) {
      setIsLoading(true);
      await updateUnsignedLicense({
        id: licenseItem.id,
        licenseName: curateString(item.name),
        organizationId: item.organization.value,
        members: item.members.map((v: { user: { value: string; label: string; memberId?: string } }) => ({
          memberId: v.user?.memberId ?? v.user.value,
          name: v.user.label,
        })),
        expirationDate: item.expirationDate,
        organizationName: item.organization.label,
      });
      setIsLoading(false);
      return;
    }

    const newGroupItem = {
      name: curateString(item.name),
      organizationId: item.organization.value,
      members: (item.members || []).map((x: any) => x.user.value),
      groupType: GroupType.Structmaster,
    } as CreateGroupItem;

    signOffLicense(newGroupItem, item.expirationDate);
  };

  const loadDisabledUsers = async (organizationId: string) => {
    const users = await roleAssignmentStore.getUsersAlreadyOnGroupType(organizationId, GroupType.Structmaster);
    if (users)
      disabledUsersRef.current = users.filter(
        (disabledUser) =>
          unsigned ||
          !licenseItem?.members.some(
            (member) => (member?.idOrEmail ?? member.memberId) === disabledUser && licenseItem.organizationId === organizationId
          )
      );
  };

  return (
    <Modal
      className="figma-modal"
      title={<h3 style={{ margin: '12px 16px' }}>{t('StructmasterLicense')}</h3>}
      open
      onOk={() => onEdit(false)}
      onCancel={() => onClose()}
      okButtonProps={{
        disabled:
          groupState.isBusy.get() ||
          formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value)).length + currentOrganizationMembers.length >
            ORGANIZATIONS_MEMBER_LIMIT,
        style: { width: '45%', float: 'right' },
      }}
      cancelButtonProps={{ disabled: groupState.isBusy.get(), style: { width: '45%' } }}
      closable={!groupState.isBusy.get()}
      maskClosable={!groupState.isBusy.get()}
      okText={t(disabled ? 'Confirm' : 'SaveChanges')}
      footer={() => (
        <>
          <Row>
            <Col>
              <Button disabled={isLoading} onClick={() => onClose()}>
                {t('Cancel')}
              </Button>
            </Col>
            <Col>
              <Button disabled={isLoading} type="primary" onClick={() => onEdit(false)}>
                {t('Save')}
              </Button>
            </Col>
            {unsigned && canApproveLicenses && (
              <Col>
                <Button disabled={isLoading} type="primary" onClick={() => onEdit(true)}>
                  {t('SaveAndSignOff')}
                </Button>
              </Col>
            )}
          </Row>
        </>
      )}
    >
      <>
        {groupState.errorMessage.value && <Alert type="error" message={t(groupState.errorMessage.get() || '')} />}
        {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
        <Spin spinning={groupState.isBusy.get() || !licenseItem || isLoading}>
          {licenseItem && (
            <Form form={form} layout="vertical" autoComplete="off">
              {formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length +
                currentOrganizationMembers.length >
                ORGANIZATIONS_MEMBER_LIMIT && (
                <Alert
                  type="error"
                  message={t('NewGroupUserCountErrorParam')
                    .replace('{0}', `${ORGANIZATIONS_MEMBER_LIMIT}`)
                    .replace('{1}', `${currentOrganizationMembers.length}`)
                    .replace(
                      '{2}',
                      `${formUsers.filter((formUser) => formUser?.value && !currentOrganizationMembers.includes(formUser?.value)).length}`
                    )}
                />
              )}
              <Alert type="warning" message={t('StructmasterLicensesOnlyOneActiveUserWarning')} />
              {formUsers.filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.value).length > 0 && (
                <Alert
                  type="warning"
                  message={
                    <>
                      {t('TheFollowingUsersWillBeAddedToTheOrganization')}
                      <br />
                      <br />
                      {formUsers
                        .filter((formUser) => !currentOrganizationMembers.includes(formUser?.value) && formUser?.label)
                        .map((v) => (
                          <>
                            - {v?.label}
                            <br />
                          </>
                        ))}
                    </>
                  }
                />
              )}
              <Tabs defaultActiveKey="1">
                <Tabs.TabPane className="ant-tabs-tabpane-without-border" tab={t('LicenseInfo')} key="1">
                  <Form.Item
                    initialValue={licenseItem.licenseName}
                    name={nameof<CreateGroupItem>('name')}
                    rules={[
                      { required: true, message: t('PleaseEnterName') },
                      () => ({
                        validator(_, value: string) {
                          if (value && value.trim().length === 0) return Promise.reject(t('PleaseEnterName'));
                          return Promise.resolve();
                        },
                      }),
                      () => ({
                        validator: (ruleObj: RuleObject, value: string) => {
                          const maxLenght = 128;
                          if (value === undefined) return Promise.resolve();
                          if (value.length > maxLenght) {
                            return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Name')).replace('{1}', `${maxLenght}`));
                          } else return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input disabled={disabled} autoComplete="off" allowClear placeholder={t('Name')} />
                  </Form.Item>
                  <Form.Item
                    name="organization"
                    initialValue={{ value: licenseItem.organizationId, label: licenseItem.organizationName }}
                    rules={[{ required: true, message: t('PleaseSelect') }]}
                  >
                    <OrganizationsSelect disabled={disabled || !editable} onChange={({ value }) => loadDisabledUsers(value)} />
                  </Form.Item>

                  <Row>
                    <Col span={12}>
                      <RoleAssignmentAttributesComponent
                        disabled={disabled || !editable}
                        showLabel={false}
                        initialValue={licenseItem.expirationDate}
                        style={{ width: '100%' }}
                        onChange={() => {}}
                      />
                    </Col>
                  </Row>
                </Tabs.TabPane>
                <Tabs.TabPane forceRender className="ant-tabs-tabpane-without-border" tab={t('Users')} key="2">
                  <Form.List
                    initialValue={licenseItem.members.map((user) => ({
                      user: { key: user.idOrEmail, value: user.idOrEmail, label: user.name, memberId: user.memberId },
                    }))}
                    name="members"
                  >
                    {(fields, { add, remove }, { errors }) => (
                      <>
                        <Form.ErrorList errors={errors} />
                        <div id="new-organization-users-container" style={{ maxHeight: '40vh', overflowX: 'auto' }}>
                          {fields.map(({ key, name: fieldIndx, fieldKey, ...restField }) => (
                            <Row key={fieldKey}>
                              <Col span={20}>
                                <Form.Item
                                  {...restField}
                                  style={{ marginRight: 10, height: '100%' }}
                                  name={[fieldIndx, 'user']}
                                  //fieldKey={[fieldKey, 'user']}
                                  rules={[
                                    () => ({
                                      required: true,
                                      validator(_, value) {
                                        if (value?.value == null) return Promise.reject(t('SelectOrInviteWarning'));
                                        if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                                        return Promise.resolve();
                                      },
                                    }),
                                    () => ({
                                      validator(_, value) {
                                        if (value?.value == null) return Promise.resolve();
                                        if (disabledUsersRef.current.includes(value.value))
                                          return Promise.reject(t('UserAlreadyIsStructmasterUserError'));
                                        return Promise.resolve();
                                      },
                                    }),
                                    (formInstance) => ({
                                      message: t('DuplicatedUserWarning'),
                                      validator(_, formItem) {
                                        const items: { user: { label: string; value: string } }[] = formInstance.getFieldValue('members');

                                        if (formItem === undefined) return Promise.resolve();

                                        if (items.filter((item) => item && item.user.value === formItem.value).length > 1) {
                                          return Promise.reject();
                                        } else {
                                          return Promise.resolve();
                                        }
                                      },
                                    }),
                                  ]}
                                >
                                  <UserAutocomplete
                                    disabled={disabled}
                                    onChange={(user) => {
                                      setFormUsers(
                                        form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user)
                                      );
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                              {!disabled && (
                                <Col span={2} style={{ textAlign: 'center' }}>
                                  <MinusCircleOutlined
                                    style={{ fontSize: 20 }}
                                    onClick={() => {
                                      setGroupUserCount(groupUserCount - 1);
                                      remove(fieldIndx);
                                      setFormUsers(
                                        form.getFieldsValue().members.map((m: { user: { label: string; value: string } | undefined }) => m?.user)
                                      );
                                    }}
                                  />
                                </Col>
                              )}
                            </Row>
                          ))}
                        </div>
                        {!disabled && (
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => {
                                add();
                                setGroupUserCount(groupUserCount + 1);
                              }}
                              block
                              icon={<PlusOutlined />}
                            >
                              {t('AddUser')}
                            </Button>
                          </Form.Item>
                        )}
                      </>
                    )}
                  </Form.List>
                </Tabs.TabPane>
              </Tabs>
            </Form>
          )}
        </Spin>
      </>
    </Modal>
  );
};

export default StructmasterLicenseGroupEdit;
